import { Accordion, Box, Button, Flex, Text, Loader } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconChevronDown } from '@tabler/icons-react';
import { useState } from 'react';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { TableHeader } from 'src/UI/common/TableHeader';
import {
  BaseFinancialItem,
  FinancialItems,
  PensionAsset,
} from 'src/financial/financial-data/hooks/useFinancialItems';
import { addAssets, AssetsAccordion } from 'src/financial/financial-data/net-worth/AssetsAccordion';
import {
  addSpendingAccounts,
  SpendingAccountsAccordion,
} from 'src/financial/financial-data/net-worth/SpendingAccountsAccordion';
import { useNavigation, useOne } from '@refinedev/core';
import { EntityModal } from 'src/financial/financial-data/EntityModal';
import {
  UpsertEntityInput,
  useUpsertEntity,
} from 'src/data-capture/merge-review/useUpsertEntities';
import { useDeleteEntities } from 'src/data-capture/merge-review/useDeleteEntities';
import {
  addLiabilities,
  LiabilitiesAccordion,
} from 'src/financial/financial-data/net-worth/LiabilitiesAccordion';
import { CreateFinancialItemDrawer } from 'src/financial/financial-data/financial-item/CreateFinancialItemDrawer';
import { EntityWithFieldMetadata } from 'src/UI/dynamicUI/types';

export default function NetWorthAccordion({
  financialItems,
  onSubmit,
  isFetching,
}: {
  financialItems: FinancialItems;
  onSubmit: () => void;
  isFetching: boolean;
}) {
  const { clientId } = useCurrentClient();
  const { push } = useNavigation();

  const [opened, { open, close }] = useDisclosure(false);
  const [activeItem, setActiveItem] = useState<string | null>(null);
  const [selectedEntity, setSelectedEntity] = useState<BaseFinancialItem | null>(null);
  const { data: entityData } = useOne<EntityWithFieldMetadata>({
    resource: `clients/${clientId}/${selectedEntity?.product}/entities`,
    id: selectedEntity?.id,
    queryOptions: {
      enabled: !!selectedEntity?.id && !!clientId && !!selectedEntity?.product,
    },
  });

  const { upsertEntities } = useUpsertEntity();
  const { deleteEntityByTypeAndId } = useDeleteEntities();

  const formatter = new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
  });

  if (!clientId) {
    return null;
  }

  const handleSubmit = async (values: UpsertEntityInput[]) => {
    if (!clientId) return;
    await upsertEntities(clientId, values, true);
    setSelectedEntity(null);
    onSubmit();
  };

  const handleDelete = async () => {
    if (!selectedEntity) {
      return;
    }

    await deleteEntityByTypeAndId(clientId, selectedEntity?.product, selectedEntity?.id);
    onSubmit();
    setSelectedEntity(null);
  };

  const handleRowClick = (financialItem: BaseFinancialItem | PensionAsset) => {
    const product =
      'pensionProduct' in financialItem ? financialItem.pensionProduct : financialItem.product;
    push(`/client/${clientId}/financial-items/${product}/${financialItem.id}`);
  };

  const { liabilities, assets, spendingAccounts, metadata } = financialItems;

  const calculateNetWorth = () => {
    return addAssets(assets) + addSpendingAccounts(spendingAccounts) - addLiabilities(liabilities);
  };

  return (
    <Box>
      <CreateFinancialItemDrawer opened={opened} close={close} />
      <Flex pos={'absolute'} right={0} top={0} align="center" gap="sm">
        {isFetching && <Loader size="sm" data-testid="net-worth-loading-spinner" />}
        <Button onClick={open} variant="pill">
          Create
        </Button>
      </Flex>
      <TableHeader
        columns={[
          {
            label: 'Type',
            align: 'left',
          },
          {
            label: 'Total',
            align: 'right',
          },
        ]}
        pl="52"
        pr="130"
      />
      <Accordion
        value={activeItem}
        onChange={setActiveItem}
        variant="cells"
        chevron={<IconChevronDown size={20} stroke={1.5} />}
      >
        {entityData?.data && selectedEntity && (
          <EntityModal
            opened={!!selectedEntity}
            onClose={() => setSelectedEntity(null)}
            entity={entityData.data}
            onSubmit={handleSubmit}
            onDelete={handleDelete}
            titleParts={[selectedEntity.product]}
          />
        )}
        <SpendingAccountsAccordion
          activeItem={activeItem}
          spendingAccounts={spendingAccounts}
          onSubmit={onSubmit}
          metadata={metadata}
          onRowClick={handleRowClick}
        />
        <AssetsAccordion
          activeItem={activeItem}
          assets={assets}
          onSubmit={onSubmit}
          metadata={metadata}
          onRowClick={handleRowClick}
        />
        <LiabilitiesAccordion
          activeItem={activeItem}
          liabilities={liabilities}
          onSubmit={onSubmit}
          metadata={metadata}
          onRowClick={handleRowClick}
        />
      </Accordion>
      <Flex direction={'row'} justify={'flex-end'} mr={128} mt="sm" p={0} gap={14}>
        <Text fw={600} m={0} c="gray.10" size="lg">
          Net worth:
        </Text>
        <Text fw={600} m={0} c="gray.10" size="lg">
          {formatter.format(calculateNetWorth())}
        </Text>
      </Flex>
    </Box>
  );
}
